import useDocumentTitle from 'hooks/useDocumentTitle'
import React, { useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Container } from 'reactstrap'
import { updateContentLibraryFilters } from 'slices/campaigns/content/reducer';
import { fetchContentLibraryThunk } from 'slices/campaigns/content/thunk';
import { AppDispatch, RootState } from 'store';
import { ContentLibraryFilters, defaultContentLibraryFilters, LibraryItem } from 'types/campaign/content';
import { decodeBase64, encodeBase64, formatNumber, generateUniqueKey, getErrorMessage } from 'utils/helpers';
import IGLogoPNG from "../../../assets/images/brands/ig-logo.png"
import Skeleton from 'react-loading-skeleton';
import FilterIcon from 'assets/icons/mono/FilterIcon';
import ContentLibraryFiltersModal from 'Components/content-library/ContentLibraryFiltersModal';
import { fetchContentLibrary } from 'services/api/content-library';
import { toast } from 'react-toastify';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import withAccessControl from 'hoc/withAccessControl';
import { PRIVILEGE } from 'constants/privilege';

const ContentLibrary = () => {

    useDocumentTitle("Content Library")
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const [filtersModalVisibility, showFiltersModal] = useState(false);
    const { filters } = useSelector((state: RootState) => state.contentLibrary);
    const [filterId, setFilterId] = useState(generateUniqueKey(filters, ['page']));
    const [autoFetchReq, setAutoFetchReq] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const queryFilters = searchParams.get('filters');
    const agencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);

    const contentLibraryData = useSelector((state: RootState) => state.contentLibrary.filtered_lists?.[filterId!]);
    const contentLibrary = contentLibraryData?.list ?? [];
    const page = contentLibraryData?.page ?? 1;
    const has_more = contentLibraryData?.has_more ?? true;
    const totalRecord = useSelector((state: RootState) => state.contentLibrary.total);

    const loading = useSelector((state: RootState) => state.contentLibrary.loading);
    const error = useSelector((state: RootState) => state.contentLibrary.error);
    const [downloading, setDownloading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    useEffect(() => {
        const decodedFilters = queryFilters ? decodeBase64(queryFilters, defaultContentLibraryFilters) : defaultContentLibraryFilters;
        dispatch(updateContentLibraryFilters({ ...decodedFilters, page: 1 }))
    }, [queryFilters])

    useEffect(() => {
        if (filters.page && filters.page === 1) {
            setInitialLoading(true);
        }
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            dispatch(fetchContentLibraryThunk({ filters })).finally(() => {
                setFilterId(generateUniqueKey(filters, ['page']));
                setInitialLoading(false);
            })
        }, 500)

    }, [filters])

    const setFilters = (updatedFilters: ContentLibraryFilters) => {
        const encodedFilters = encodeBase64(updatedFilters);
        setSearchParams({ filters: encodedFilters });
    }

    const nextPage = () => {
        if (has_more && !loading) {
            const nextPageNumber = page + 1;
            dispatch(fetchContentLibraryThunk({ filters: { ...filters, page: nextPageNumber } }))
        }
    }

    const downloadSheet = async () => {
        if (!agencyId || !totalRecord || downloading) return;
        try {
            setDownloading(true);
            const { items }: { items: LibraryItem[] } = await fetchContentLibrary(agencyId, { page: 1, limit: totalRecord });
            const rows = items.map((item) => ({
                Link: item.social_post?.permalink ?? "",
                User: item.social_post?.social_account?.handle ?? "",
                Likes: item.social_post?.like_count ?? 0,
                Comments: item.social_post?.comments_count ?? 0,
                Views: item.social_post?.views_count ?? 0,
                "Posted on": item.social_post?.posted_on
            }));

            const worksheet = XLSX.utils.json_to_sheet(rows);

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "ContentLibrary");

            const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });

            const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
            saveAs(blob, `content_library_${new Date().toISOString().split("T")[0]}.xlsx`);
        } catch (error) {
            console.error(error);
            toast.error(getErrorMessage(error, "Failed to download file!"));
        } finally {
            setDownloading(false);
        }
    };


    return (
        <div className="campaign-page">
            <div className="d-flex align-items-center justify-content-between">
                <h1 className="campaign-page-title">Content Library</h1>
                <div className="d-flex align-items-center justify-content-end gap-2">
                    {
                        totalRecord > 0 &&
                        <button disabled={downloading} className='btn btn-white' onClick={() => downloadSheet()}> 
                            {
                                downloading ? (
                                    <span>Downloading...</span>
                                ) : (
                                    <span><i className='ri-file-download-line fs-5 me-1'></i> Download</span>
                                )
                            }
                        </button>
                    }
                    <button className='btn btn-white' onClick={() => showFiltersModal(true)}><FilterIcon className="me-1" /> Filters</button>
                </div>
            </div>

            <div className="card campaign-insights-card mt-4">
                <div className="card-body p-0 m-0">
                    <InfiniteScroll
                        dataLength={contentLibrary.length}
                        next={nextPage}
                        hasMore={has_more}
                        loader={<></>}
                        className="w-100"
                    >
                        <table>
                            <thead>
                                <tr>
                                    <th>Post</th>
                                    {/* <th className='text-start ps-4'>Campaign</th> */}
                                    <th className='text-start ps-4'>Likes</th>
                                    <th className='text-start ps-4'>Comments</th>
                                    <th className='text-start ps-4'>Views</th>
                                    {/* <th className='text-start ps-4'>Amount</th> */}
                                    {/* <th className='text-start ps-4'>Posted on</th> */}
                                </tr>
                            </thead>
                            <tbody>
                                <tr className='border-bottom-1'>
                                    <td colSpan={4}>
                                        <div>
                                            Showing {contentLibrary.length} of {totalRecord} Records
                                        </div>
                                    </td>
                                </tr>
                                {
                                    !initialLoading && contentLibrary.map((libraryItem, i) => {
                                        return (
                                            <tr className="border-bottom-1 px-3" key={i}>

                                                <td>
                                                    <div className="py-3">
                                                        <div className="d-flex justify-content-start align-items-center gap-3 border-right-dashed td-content">
                                                            <a href={libraryItem.social_post.permalink} target='_blank' rel='noreferrer'><div className='bgc-content bg-image cursor-pointer' style={{ backgroundImage: `url(${libraryItem.social_post.thumbnail_url})`, height: '60px', width: '40px', borderRadius: '4px' }}></div></a>
                                                            <div className="d-flex flex-column justify-content-center">
                                                                <div className="d-flex align-items-center gap-1">
                                                                    {
                                                                        libraryItem.social_post.platform === 'ig' &&
                                                                        <img src={IGLogoPNG} alt="IG Logo" className='linked-posts-ig-logo' />
                                                                    }
                                                                    {
                                                                        libraryItem.social_post.platform === 'yt' &&
                                                                        <i className='ri-youtube-fill text-danger linked-posts-yt-logo mt-1'></i>
                                                                    }

                                                                    <span className='linked-posts-ig-username'>{libraryItem.social_post.social_account.handle}</span>
                                                                </div>
                                                                <span className='linked-post-posted-on'>{libraryItem.social_post.posted_on}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                {/* <td className='ps-4'>
                                                    <div className="py-3">
                                                        <div className="d-flex justify-content-start align-items-center gap-1 border-right-dashed td-content">
                                                            <span className=""> <i className='bx bxs-megaphone me-1'></i> {libraryItem.campaign.name}</span>
                                                        </div>
                                                    </div>
                                                </td> */}
                                                <td className='ps-4'>
                                                    <div className="py-3">
                                                        <div className="d-flex justify-content-start align-items-center gap-1 border-right-dashed td-content">
                                                            <span className=""> <i className='ri-heart-line me-1'></i> {formatNumber(Number(libraryItem.social_post?.like_count ?? 0))}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className='ps-4'>
                                                    <div className="py-3">
                                                        <div className="d-flex justify-content-start align-items-center gap-1 border-right-dashed td-content">
                                                            <span className=""> <i className='ri-chat-3-line me-1'></i> {formatNumber(Number(libraryItem.social_post?.comments_count ?? 0))}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className='ps-4'>
                                                    <div className="py-3">
                                                        <div className="d-flex justify-content-start align-items-center gap-1 td-content">
                                                            <span className="">  <i className='ri-eye-line me-1'></i>  {formatNumber(Number(libraryItem.social_post?.views_count ?? 0))}</span>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* <td className='ps-4'>
                                                    <div className="py-3">
                                                        <div className="d-flex justify-content-start align-items-center gap-1 border-right-dashed td-content">
                                                            <span className=""> <i className='bx bx-rupee me-1'></i> {formatNumber(Number(libraryItem.payments_sum_amount ?? 0))}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className='ps-4'>
                                                    <div className="py-3">
                                                        <div className="d-flex justify-content-start align-items-center gap-1 td-content">
                                                            <span className=""> <i className=' ri-calendar-line me-1'></i> {libraryItem.social_post.posted_on}</span>
                                                        </div>
                                                    </div>
                                                </td> */}
                                            </tr>
                                        )
                                    })
                                }
                                {
                                    !initialLoading && !loading &&
                                    <td colSpan={4}>
                                        <div>
                                            Showing {contentLibrary.length} of {totalRecord} Records
                                        </div>
                                    </td>
                                }
                                
                                {
                                    (loading || initialLoading) && !autoFetchReq &&
                                    Array.from({ length: 10 }).map((_, i) => {
                                        return (
                                            <tr>
                                                <td><Skeleton /></td>
                                                <td><Skeleton /></td>
                                                <td><Skeleton /></td>
                                                <td><Skeleton /></td>
                                                {/* <td><Skeleton /></td>
                                                <td><Skeleton /></td>
                                                <td><Skeleton /></td> */}
                                            </tr>
                                        )
                                    })
                                }
                            </tbody>
                        </table>
                        {
                            !(loading || initialLoading) && error &&
                            <div className="py-3 d-flex justify-content-center align-items-center">
                                <button className="theme-btn theme-btn-sm btn-primary-outlined" onClick={nextPage}>Load More <i className='ms-2 fs-5 bx bx-refresh'></i></button>
                            </div>
                        }
                        {
                            !(loading || initialLoading) && contentLibrary.length === 0 &&
                            <div className="d-flex w-100 justify-content-center align-items-center my-5">
                                <div className="length-zero my-5">
                                    <h3>Currently there is no item in your list</h3>
                                </div>
                            </div>
                        }
                    </InfiniteScroll>
                    <ContentLibraryFiltersModal
                        isOpen={filtersModalVisibility}
                        onClose={() => showFiltersModal(false)}
                        defaultFilters={filters}
                        onApply={(_filters) => {
                            setFilters({ ..._filters, page: 1, sort: filters.sort })
                        }}
                    />
                </div>
            </div>
        </div>
    )
}

export default withAccessControl(ContentLibrary, PRIVILEGE.CAMPAIGN.COLLABORATION.INDEX)